/* FICHIER: DashboardPage.jsx — version avec modale “Mon compte” centrée
   - OTA / online/offline : inchangé (sauf OTA UI)
   - OTA UI: spinner indéterminé tant que pas de pourcentage, barre dès qu’on reçoit pct
   - Modale “Mon compte” : remplit username / email / created_at
   - Déconnexion depuis la modale
   - Bouton "Changer le mot de passe" (redirige, à adapter)
*/
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import DeviceCard from './DeviceCard';
import DeviceListBox from './DeviceListBox';
// import AccountMenu from './AccountMenu'; // plus utilisé (modale centrée)
import NewDeviceWizard from './NewDeviceWizard';
import { API_BASE } from '../config/api';
import client from '../mqttService';
import Select from 'react-select';
import { FlagIcon } from 'react-flag-kit';

// CSS externes
import './dashboard.css';
import './modals.css';

// ===== Debug switch =====
const DEBUG = false;
const dlog  = (...a) => DEBUG && console.log(...a);
const dwarn = (...a) => DEBUG && console.warn(...a);

// Fenêtre de grâce offline
const OFFLINE_GRACE_MS = 90_000;
// Pop-up (ON/OFF)
const POPUP_AUTO_HIDE_MS = 30_000;
// Vérif firmware
const FW_CHECK_IV_MS = 20 * 60 * 1000;
// Fenêtre OTA pour accepter "start/queued"
const OTA_WINDOW_MS = 180000;

const FW_GET_CURRENT = (model) =>
  `${API_BASE}/firmware/get_current.php?model=${encodeURIComponent(model || '')}&ts=${Date.now()}`;
const FW_GET_LATEST = (model) =>
  `${API_BASE}/firmware/get_latest.php?model=${encodeURIComponent(model || '')}&ts=${Date.now()}`;
const FW_REQUEST_OTA = `${API_BASE}/firmware/request_ota.php`;
const FW_OTA_PROGRESS = null; // (HTTP polling optionnel, non utilisé ici)

const COUNTRY_OPTIONS = [
  { value: 'FR', label: 'France', flag: 'FR' },
  { value: 'IT', label: 'Italie', flag: 'IT' },
  { value: 'ES', label: 'Espagne', flag: 'ES' },
  { value: 'DE', label: 'Allemagne', flag: 'DE' },
  { value: 'US', label: 'États-Unis', flag: 'US' },
  { value: 'GB', label: 'Royaume-Uni', flag: 'GB' },
  { value: 'CA', label: 'Canada', flag: 'CA' }
];

const normalizeMac = (s) => String(s || '').replace(/[^a-fA-F0-9]/g, '').toUpperCase();
const extractVersion = (s) => {
  const m = String(s || '').match(/(\d+\.\d+\.\d+)/);
  return m ? m[1] : '';
};
const extractModel = (s) => {
  const m = String(s || '').match(/^([A-Za-z0-9_-]+)[ _-]\d+\.\d+\.\d+/);
  return m ? m[1].replace(/-+/g, '_') : '';
};
const cmpVer = (a, b) => {
  const pa = String(a || '').split('.').map(n => parseInt(n, 10) || 0);
  const pb = String(b || '').split('.').map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i++) {
    if (pa[i] > pb[i]) return 1;
    if (pa[i] < pb[i]) return -1;
  }
  return 0;
};
const parseLatestFromResp = (j) => {
  const name = j?.latest_firmware_name || j?.file || j?.name || '';
  return j?.latest_version || j?.version || extractVersion(name) || '';
};

// -------- helpers UI compte --------
function safeToLocaleDate(val) {
  if (!val) return '—';
  // Gérer format SQL "YYYY-MM-DD HH:MM:SS"
  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const [_, y, mo, d] = m;
    return `${d}-${mo}-${y}`;
  }
  // Sinon, fallback ISO ou autre
  const isoish = String(val).replace(' ', 'T');
  const d = new Date(isoish);
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

async function fetchJSON(url) {
  const r = await fetch(url, { credentials: 'include', headers: { 'Cache-Control': 'no-cache' }});
  let j = null;
  try { j = await r.json(); } catch {}
  return { ok: r.ok, json: j };
}


// ===== OTA helpers (nouvelle logique spinner → barre) =====
function extractPercent(text) {
  const m = String(text || '').match(/(\d{1,3})\s*%/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (Number.isFinite(n)) return Math.max(0, Math.min(100, n));
  return null;
}
function parseOtaStatusMessage(msgStr) {
  try {
    const j = JSON.parse(msgStr);
    const state = String(j?.state || j?.status || '').toLowerCase();
    const progress = j?.progress ?? j?.percent ?? j?.percentage ?? j?.pct;
    return { state, progress };
  } catch {
    const s = String(msgStr || '').trim().toLowerCase();
    const p = extractPercent(s) ?? undefined;
    let state = 'unknown';
    if (s.includes('queued')) state = 'queued';
    else if (s.includes('start')) state = 'start';
    else if (s.includes('download')) state = 'download';
    else if (s.includes('flash')) state = 'flash';
    else if (s.includes('reboot')) state = 'reboot';
    else if (s.includes('success') || s.includes('done')) state = 'success';
    else if (s.includes('error') || s.includes('failed')) state = 'error';
    return { state, progress: p };
  }
}

function mapOtaProgress(payload) {
  const state = String(payload?.state || payload?.status || '').toLowerCase();
  const raw = payload?.progress ?? payload?.percent ?? payload?.percentage ?? payload?.pct;
  const hasPct = raw != null && !Number.isNaN(Number(raw));
  const p = hasPct ? Math.max(0, Math.min(100, Number(raw))) : 0;

  // Indéterminé tant que l’ESP n’a pas publié de pourcentage
  const indeterminate = !hasPct && ['queued', 'start', 'download'].includes(state);

  if (state === 'done' || state === 'success') {
    return { progress: 100, status: 'done', updating: false, indeterminate: false };
  }
  if (state === 'error' || state === 'failed') {
    return { progress: p, status: 'error', updating: false, indeterminate: false, error: 'ota_failed' };
  }
  if (state === 'reboot') {
    return { progress: hasPct ? Math.max(95, p) : 95, status: 'reboot', updating: true, indeterminate: false };
  }
  if (state === 'flash') {
    return { progress: hasPct ? Math.max(80, p) : 80, status: 'flash', updating: true, indeterminate: false };
  }
  // queued / start / download (sans pct) => spinner
  if (indeterminate) {
    return { progress: 0, status: state, updating: true, indeterminate: true };
  }
  // Sinon, on a un chiffre (download avec pct, etc.)
  return { progress: p, status: state || 'unknown', updating: p < 100, indeterminate: false };
}

const hasOtaWindow = (ref, mac) => (ref.current[mac] || 0) > Date.now();
const openOtaWindow = (ref, mac, ms = OTA_WINDOW_MS) => { ref.current[mac] = Date.now() + ms; };

function flattenDevices(groupedDevices) {
  if (!Array.isArray(groupedDevices)) return [];
  return groupedDevices.flatMap(group => Array.isArray(group.devices) ? group.devices : []);
}

function DashboardPage({ user, onLogout, onDeviceClick }) {
  // responsive
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handleResize = () => windowWidth !== window.innerWidth && setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [windowWidth]);

  const [myDevices, setMyDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [relayStates, setRelayStates] = useState({});
  const [onlineStates, setOnlineStates] = useState({});

  // UI états
  const [selectedClaimedId, setSelectedClaimedId] = useState(null);
  const [showWizard, setShowWizard] = useState(false);

  // === Compte (modale centrée) ===
  const [accountOpen, setAccountOpen] = useState(false);
  
  const [showPwModal, setShowPwModal] = useState(false);
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
const [account, setAccount] = useState({ username: '', email: '', created_at: '' });
  const [accountLoading, setAccountLoading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({});

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);
  const localActionRef = useRef({});
  const prevRelayRef = useRef({});
  const playDing = useCallback((freq = 880) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t0 = ctx.currentTime;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.3, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
      osc.start();
      osc.stop(t0 + 0.2);
      setTimeout(() => ctx.close(), 300);
    } catch {}
  }, []);
  const showToast = useCallback((id, name, isOn) => {
    if (toastTimerRef.current) { clearTimeout(toastTimerRef.current); toastTimerRef.current = null; }
    setToast({ id, name, isOn });
    playDing(isOn ? 900 : 600);
    toastTimerRef.current = setTimeout(() => setToast(null), POPUP_AUTO_HIDE_MS);
  }, [playDing]);

  // mapping MAC -> id
  const macToDeviceId = useMemo(() => {
    const mapping = {};
    flattenDevices(myDevices).forEach(device => {
      const mac = normalizeMac(device?.identifier);
      if (mac) mapping[mac] = device.id;
    });
    return mapping;
  }, [myDevices]);
  const idToName = useMemo(() => {
    const map = {};
    flattenDevices(myDevices).forEach(d => {
      const label = d?.name || d?.device_name || `Appareil ${d?.id}`;
      map[String(d?.id)] = label;
      map[Number(d?.id)] = label;
    });
    return map;
  }, [myDevices]);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const otaWindowRef = useRef({}); // { [MAC]: expiresTs }

  // --------- ONLINE polling ---------
  useEffect(() => {
    function softMergeOnline(fromApiArray) {
      const now = Date.now();
      setOnlineStates(prev => {
        let changed = false;
        const next = { ...prev };
        fromApiArray.forEach(({ id, online }) => {
          const incoming = Number(online) === 1;
          const prevEntry = prev[id];
          if (!prevEntry) {
            next[id] = { online: incoming, lastSeen: incoming ? now : 0 };
            changed = true;
            return;
          }
          if (incoming) {
            if (!prevEntry.online || now - prevEntry.lastSeen > 0) {
              next[id] = { online: true, lastSeen: now };
              changed = true;
            }
          } else {
            if (prevEntry.online && now - prevEntry.lastSeen > OFFLINE_GRACE_MS) {
              next[id] = { online: false, lastSeen: prevEntry.lastSeen };
              changed = true;
            }
          }
        });
        return changed ? next : prev;
      });
    }
    function fetchOnlineStates() {
      fetch(`${API_BASE}/devices/get_relays_state.php`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => Array.isArray(data) && softMergeOnline(data))
        .catch(e => console.error('[OnlineStates] fetch error:', e));
    }
    fetchOnlineStates();
    const interval = setInterval(fetchOnlineStates, 120000);
    return () => clearInterval(interval);
  }, []);

  // --------- DEVICES polling ---------
  const fetchMyDevices = useCallback(() => {
    return fetch(`${API_BASE}/devices/get_my_devices.php`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        setMyDevices(prev => {
          try { return JSON.stringify(prev) === JSON.stringify(data) ? prev : data; }
          catch { return data; }
        });
        setRelayStates(prev => {
          const next = { ...prev };
          flattenDevices(data).forEach(d => {
            if (typeof next[d.id] === 'undefined' && typeof d.relay_state !== 'undefined') {
              next[d.id] = d.relay_state === 1 || d.relay_state === '1';
            }
          });
          return next;
        });
        const now = Date.now();
        setOnlineStates(prev => {
          let changed = false;
          const next = { ...prev };
          flattenDevices(data).forEach(d => {
            const incoming = Number(d.online) === 1;
            const prevEntry = prev[d.id];
            if (!prevEntry) {
              next[d.id] = { online: incoming, lastSeen: incoming ? now : 0 };
              changed = true;
              return;
            }
            if (incoming) {
              if (!prevEntry.online || now - prevEntry.lastSeen > 0) {
                next[d.id] = { online: true, lastSeen: now };
                changed = true;
              }
            } else {
              if (prevEntry.online && now - prevEntry.lastSeen > OFFLINE_GRACE_MS) {
                next[d.id] = { online: false, lastSeen: prevEntry.lastSeen };
                changed = true;
              }
            }
          });
          return changed ? next : prev;
        });
      })
      .catch(e => console.error('[MyDevices] fetch error:', e));
  }, []);
  const myDevicesRef = useRef([]);
  useEffect(() => { myDevicesRef.current = myDevices; }, [myDevices]);
  useEffect(() => {
    fetchMyDevices().finally(() => setLoadingDevices(false));
    const interval = setInterval(fetchMyDevices, 20000);
    return () => clearInterval(interval);
  }, [fetchMyDevices]);

  // MQTT base
  useEffect(() => {
    if (!client) {
      console.error('[MQTT][Dashboard] client MQTT undefined');
      return;
    }
    const onConnect = () => dlog('[MQTT][Dashboard] Connecté');
    const onClose   = () => dwarn('[MQTT][Dashboard] Fermé');
    const onError   = (err) => console.error('[MQTT][Dashboard] Erreur MQTT:', err);
    const onOffline = () => dwarn('[MQTT][Dashboard] Offline');
    client.on('connect', onConnect);
    client.on('close', onClose);
    client.on('error', onError);
    client.on('offline', onOffline);
    return () => {
      client.off('connect', onConnect);
      client.off('close', onClose);
      client.off('error', onError);
      client.off('offline', onOffline);
    };
  }, []);

  // ========= MQTT: state + status (Sonoff + Tasmota) =========
useEffect(() => {
  if (!client) return;

  const macs = flattenDevices(myDevices)
    .map(d => normalizeMac(d?.identifier))
    .filter(Boolean);

  const topics = [];
  macs.forEach(mac => {
    topics.push(`sonoff/state/${mac}`);
    topics.push(`sonoff/status/${mac}`);
  });

  // Abonnements Tasmota : ON/OFF + LWT + STATE périodique
  topics.push('tasmota/stat/+/POWER');
  topics.push('tasmota/stat/+/RESULT');
  topics.push('tasmota/tele/+/LWT');
  topics.push('tasmota/tele/+/STATE');

  const handleMqttMessage = (topic, message) => {
    const msgStr = (message?.toString?.() || '').trim();

    // === SONOFF relay ===
    let m = topic.match(/^sonoff\/state\/([^/]+)$/i);
    if (m) {
      const mac = m[1].toUpperCase();
      const deviceId = macToDeviceId[mac];
      if (!deviceId) return;
      const isOn = msgStr.toUpperCase() === 'ON';
      setRelayStates(prev => ({ ...prev, [deviceId]: isOn }));
      return;
    }

    // === SONOFF online/offline ===
    m = topic.match(/^sonoff\/status\/([^/]+)$/i);
    if (m) {
      const mac = m[1].toUpperCase();
      const deviceId = macToDeviceId[mac];
      if (!deviceId) return;
      const isOnline = msgStr.toLowerCase() === 'online';
      setOnlineStates(prev => ({
        ...prev,
        [deviceId]: {
          online: isOnline,
          lastSeen: isOnline ? Date.now() : (prev[deviceId]?.lastSeen ?? 0)
        }
      }));
      return;
    }

    // === TASMOTA relay: POWER or RESULT ===
    m = topic.match(/^tasmota\/stat\/([^/]+)\/(POWER|RESULT)$/i);
    if (m) {
      const topicPart = m[1];
      const mac = Object.keys(macToDeviceId).find(macKey =>
        topicPart.toUpperCase().includes(macKey.slice(-6))
      );
      if (!mac) return;
      const deviceId = macToDeviceId[mac];
      if (!deviceId) return;
      try {
        const j = JSON.parse(msgStr);
        const pwr = String(j?.POWER || j?.Power || msgStr).toUpperCase();
        if (pwr === 'ON' || pwr === 'OFF') {
          const isOn = pwr === 'ON';
          setRelayStates(prev => {
            const changed = prev[deviceId] !== isOn;
            if (changed) {
              const now = Date.now();
              const lastLocal = localActionRef.current[deviceId];
              const ignore = lastLocal && now - lastLocal < 4000;
              if (!ignore) {
                const name = idToName[deviceId] || `Appareil ${deviceId}`;
                showToast(deviceId, name, isOn);
              }
            }
            return { ...prev, [deviceId]: isOn };
          });
        }
      } catch (e) {
        console.warn('[MQTT][Tasmota] parse error POWER/RESULT:', e);
      }
      return;
    }

    // === TASMOTA periodic STATE ===
m = topic.match(/^tasmota\/tele\/([^/]+)\/STATE$/i);
if (m) {
  try {
    const j = JSON.parse(msgStr);
    const topicPart = m[1];
    const mac = Object.keys(macToDeviceId).find(macKey =>
      topicPart.toUpperCase().includes(macKey.slice(-6))
    );
    if (!mac) return;
    const deviceId = macToDeviceId[mac];
    if (!deviceId) return;
    const pwr = String(j?.POWER || j?.Power || '').toUpperCase();
    if (pwr === 'ON' || pwr === 'OFF') {
      const isOn = pwr === 'ON';
      setRelayStates(prev => {
        const changed = prev[deviceId] !== isOn;
        if (changed) {
          const now = Date.now();
          const lastLocal = localActionRef.current[deviceId];
          const ignore = lastLocal && now - lastLocal < 4000;
          if (!ignore) {
            const name = idToName[deviceId] || `Appareil ${deviceId}`;
            showToast(deviceId, name, isOn);
          }
        }
        return { ...prev, [deviceId]: isOn };
      });
    }
  } catch (e) {
    console.warn('[MQTT][Tasmota] parse error STATE:', e);
  }
  return;
}

    // === TASMOTA online/offline (LWT) ===
    m = topic.match(/^tasmota\/tele\/([^/]+)\/LWT$/i);
    if (m) {
      const topicPart = m[1];
      const mac = Object.keys(macToDeviceId).find(macKey =>
        topicPart.toUpperCase().includes(macKey.slice(-6))
      );
      if (!mac) return;
      const deviceId = macToDeviceId[mac];
      if (!deviceId) return;
      const isOnline = msgStr.toUpperCase() === 'ONLINE';
      setOnlineStates(prev => ({
        ...prev,
        [deviceId]: {
          online: isOnline,
          lastSeen: isOnline ? Date.now() : (prev[deviceId]?.lastSeen ?? 0)
        }
      }));
      return;
    }
  };

  const sub = () => {
    topics.forEach(t => client.subscribe(t, err => {
      if (err) console.error('[MQTT] subscribe error:', t, err?.message || err);
    }));
    client.on('message', handleMqttMessage);
  };

  if (client.connected && !client.disconnecting) sub();
  else client.once('connect', sub);

  return () => {
    client.off('message', handleMqttMessage);
    if (client.connected && !client.disconnecting) {
      topics.forEach(t => client.unsubscribe(t, () => {}));
    }
  };
}, [myDevices, macToDeviceId, idToName, showToast]);

// ======= Pop-up ON/OFF =======
useEffect(() => {
  const prev = prevRelayRef.current;
  const now = Date.now();
  Object.keys(relayStates).forEach(idStr => {
    const id = Number(idStr);
    const newVal = relayStates[id];
    const oldVal = prev[id];
    if (typeof newVal === 'boolean' && typeof oldVal === 'boolean' && newVal !== oldVal) {
      const lastLocal = localActionRef.current[id];
      const ignore = lastLocal && now - lastLocal < 4000;
      if (!ignore) {
        const displayName = idToName[idStr] || idToName[id] || `Appareil ${id}`;
        showToast(id, displayName, newVal);
      }
    }
  });
  prevRelayRef.current = relayStates;
}, [relayStates, idToName, showToast]);

// ======= Commande ON/OFF =======
const toggleRelay = (deviceId, currentState) => {
  const dev = flattenDevices(myDevices).find(d => String(d.id) === String(deviceId));
  const mac = normalizeMac(dev?.identifier);
  if (!mac) return;
  const newState = !currentState;
  localActionRef.current[deviceId] = Date.now();
  setRelayStates(prev => ({ ...prev, [deviceId]: newState }));
  fetch(`${API_BASE}/devices/mqtt_send.php?mac=${mac}&state=${newState ? 'ON' : 'OFF'}`, { credentials: 'include' })
    .then(() => {
      setTimeout(() => {
        setRelayStates(prev => (prev[deviceId] !== newState ? { ...prev, [deviceId]: currentState } : prev));
      }, 2000);
    })
    .catch(() => {
      setRelayStates(prev => ({ ...prev, [deviceId]: currentState }));
      alert("Erreur lors de l'envoi de la commande MQTT ON/OFF.");
    });
};

// ======= Suppression d’un appareil =======
const handleDelete = async (id) => {
  if (!window.confirm('Supprimer cet appareil ?')) return;
  await fetch(`${API_BASE}/devices/delete_device.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id }),
    credentials: 'include'
  });
  setMyDevices(prev =>
    prev.map(group => ({ ...group, devices: group.devices.filter(d => d.id !== id) }))
  );
  setSelectedClaimedId(null);
  setDeleteId(null);
  setShowDeleteDialog(false);
  fetchMyDevices();
};

// ======= Liste pour la modale de suppression =======
const deleteOptions = useMemo(() => {
  return flattenDevices(myDevices).map(d => ({
    value: d.id,
    label: `${d.name || d.device_name || `Appareil ${d.id}`} • ${normalizeMac(d.identifier)}`
  }));
}, [myDevices]);


  // ===== Découverte / appairage ===== (inchangé)
  const [unclaimed, setUnclaimed] = useState([]);
  const [loadingUnclaimed, setLoadingUnclaimed] = useState(false);
  const [pairingMac, setPairingMac] = useState(null);
  const [pairMsg, setPairMsg] = useState(null);
  const [manualMac, setManualMac] = useState('');
  const [manualError, setManualError] = useState('');
  const fmtAge = (sec) => {
    if (sec == null) return '';
    const s = Number(sec) || 0;
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60), r = s % 60;
    return `${m}m${r.toString().padStart(2,'0')}`;
  };
  const fetchUnclaimed = useCallback(() => {
    setLoadingUnclaimed(true);
    const url = `${API_BASE}/devices/get_unclaimed_devices.php?minutes=15&ts=${Date.now()}`;
    return fetch(url, {
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    })
      .then(r => r.json())
      .then(arr => {
        const list = Array.isArray(arr) ? arr : [];
        const byId = {};
        for (const raw of list) {
          const mac = normalizeMac(raw.identifier);
          if (!mac) continue;
          const item = { ...raw, identifier: mac };
          const prev = byId[mac];
          if (!prev) byId[mac] = item;
          else {
            const prevLs = prev.last_seen || '';
            const curLs  = item.last_seen || '';
            if (curLs > prevLs) byId[mac] = item;
          }
        }
        const dedup = Object.values(byId);
        dedup.sort((a,b) => {
          if (a.last_seen && b.last_seen) return a.last_seen > b.last_seen ? -1 : 1;
          if (typeof a.age_sec === 'number' && typeof b.age_sec === 'number') return a.age_sec - b.age_sec;
          return 0;
        });
        setUnclaimed(dedup);
      })
      .catch(e => console.error('[Unclaimed] fetch error:', e))
      .finally(() => setLoadingUnclaimed(false));
  }, []);
  useEffect(() => {
    if (!showWizard) return;
    fetchUnclaimed();
    const iv = setInterval(fetchUnclaimed, 7000);
    return () => clearInterval(iv);
  }, [showWizard, fetchUnclaimed]);

  const doPair = useCallback(async (rawMac) => {
    const mac = normalizeMac(rawMac);
    setPairMsg(null);
    setPairingMac(mac);
    try {
      const body = new URLSearchParams({ identifier: mac });
      const res = await fetch(`${API_BASE}/devices/claim_ack.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'include',
        body
      });
      const json = await res.json();
      if (!json?.ok) throw new Error(json?.error || 'ack_failed');
      setPairMsg('ACK envoyé. Attente du device (10–30s)…');
      const deadline = Date.now() + 35000;
      let seen = false;
      while (Date.now() < deadline && !seen) {
        await new Promise(r => setTimeout(r, 3000));
        await fetchMyDevices();
        const flat = flattenDevices(myDevicesRef.current);
        seen = flat.some(d => normalizeMac(d.identifier) === mac);
      }
      if (seen) {
        setPairMsg('✅ Appareil ajouté !');
        setTimeout(() => { setShowWizard(false); setPairMsg(null); }, 1500);
      } else {
        setPairMsg('ℹ️ ACK reçu. Le device ne s’est pas encore présenté. Vérifie son alimentation / Wi-Fi.');
      }
    } catch (e) {
      setPairMsg(`❌ Erreur: ${e.message || e}`);
    } finally {
      setPairingMac(null);
      fetchUnclaimed();
    }
  }, [fetchMyDevices, fetchUnclaimed]);

  // ===== Firmware (check + UI + OTA) =====
  const [fwById, setFwById] = useState({});
  const [fwPromptForId, setFwPromptForId] = useState(null);
  const fwLastCheckRef = useRef({});
  const fwByIdRef = useRef({});
  useEffect(() => { fwByIdRef.current = fwById; }, [fwById]);

  const setFwState = (id, patch) => {
    if (!mountedRef.current) return;
    setFwById(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const checkDeviceFw = useCallback(async (device, force = false) => {
    if (!device) return;
    const id = device.id;
    const now = Date.now();
    const last = fwLastCheckRef.current[id] || 0;
    if (!force && now - last < 60_000) return;
    fwLastCheckRef.current[id] = now;

    const fwName = device.firmware_name || device.firmware || '';
    const rawCurrent = device.firmware_version || device.firmware || fwName || '';
    const currentVersion = extractVersion(rawCurrent) || extractVersion(fwName) || '';
    const model = extractModel(fwName) || 'LDJ_MiniR2';

    setFwState(id, { checking: true, error: null, current: currentVersion });

    const updateStateFromLatest = (latest) => {
      if (!latest || !currentVersion) {
        setFwState(id, { checking: false, hasUpdate: false, latest, current: currentVersion });
        return;
      }
      const hasUpdate = cmpVer(latest, currentVersion) > 0;
      setFwState(id, { checking: false, hasUpdate, latest, current: currentVersion });
    };

    try {
      const r1 = await fetch(FW_GET_CURRENT(model), { credentials: 'include', headers: { 'Cache-Control': 'no-cache' } });
      if (r1.ok) { const j1 = await r1.json(); updateStateFromLatest(parseLatestFromResp(j1)); return; }
    } catch {}
    try {
      const r2 = await fetch(FW_GET_LATEST(model), { credentials: 'include', headers: { 'Cache-Control': 'no-cache' } });
      const j2 = await r2.json(); updateStateFromLatest(parseLatestFromResp(j2));
    } catch {
      setFwState(id, { checking: false, error: 'fw_check_failed' });
    }
  }, []);

  useEffect(() => {
    const flat = flattenDevices(myDevices);
    if (!flat.length) return;
    flat.forEach(d => {
      const online = onlineStates[d.id]?.online ?? (Number(d.online) === 1);
      if (online) checkDeviceFw(d);
    });
    const iv = setInterval(() => {
      const again = flattenDevices(myDevicesRef.current);
      again.forEach(d => {
        const online = onlineStates[d.id]?.online ?? (Number(d.online) === 1);
        if (online) checkDeviceFw(d);
      });
    }, FW_CHECK_IV_MS);
    return () => clearInterval(iv);
  }, [myDevices, onlineStates, checkDeviceFw]);

  // MQTT: ota_status + info + claim + ota (fenêtre)
  useEffect(() => {
    if (!client) return;
    const topics = ['sonoff/ota_status/+', 'sonoff/info/+', 'sonoff/claim/', 'sonoff/claim/+', 'sonoff/ota/+'];

    const handler = (topic, message, packet) => {
      const msgStr = message?.toString?.() ?? '';

      const mOrder = topic.match(/^sonoff\/ota\/([^/]+)$/i);
      if (mOrder) {
        const mac = String(mOrder[1]).toUpperCase();
        let recent = true;
        try {
          const j = JSON.parse(msgStr);
          if (j?.ts && typeof j.ts === 'number' && packet?.retain && (Date.now() - (j.ts * 1000)) > 60000) {
            recent = false;
          }
        } catch {}
        if (!packet?.retain || recent) openOtaWindow(otaWindowRef, mac);
        return;
      }

      const mOta = topic.match(/^sonoff\/ota_status\/([^/]+)$/i);
      if (mOta) {
        const mac = String(mOta[1]).toUpperCase();
        const deviceId = macToDeviceId[mac];
        if (!deviceId) return;

        const parsed = parseOtaStatusMessage(msgStr);
        const state  = String(parsed.state || '').toLowerCase();
        const hasPct = parsed.progress != null && !Number.isNaN(Number(parsed.progress));

        const s = fwByIdRef.current[deviceId] || {};
        const windowOk = hasOtaWindow(otaWindowRef, mac);
        const hasContext = !!(s.updating || s.target || windowOk);
        if (!hasContext && (state === 'start' || state === 'queued') && !hasPct) return;

        const mapped = mapOtaProgress(parsed);
        const now = Date.now();
        setFwState(deviceId, { ...mapped, lastTs: now });

        if (mapped.status === 'done' && mapped.progress === 100) {
          setFwState(deviceId, { updating: false, progress: 0, status: 'done', indeterminate: false, lastTs: now, target: null });
          const dev = flattenDevices(myDevicesRef.current).find(d => d.id === deviceId);
          if (dev) setTimeout(() => checkDeviceFw(dev, true), 15000);
        }
        return;
      }

      const mInfo = topic.match(/^sonoff\/info\/([^/]+)$/i);
      if (mInfo) {
        const mac = String(mInfo[1]).toUpperCase();
        const deviceId = macToDeviceId[mac];
        if (!deviceId) return;

        let info = null;
        try { info = JSON.parse(msgStr); } catch { info = null; }
        const fwName = info?.firmware_name || info?.firmware || '';
        const fwVer  = info?.firmware_version || extractVersion(fwName) || '';
        if (!fwVer) return;

        const state = fwByIdRef.current[deviceId] || {};
        const now = Date.now();

        if (state.target && (fwName === state.target || extractVersion(state.target) === fwVer)) {
          setFwState(deviceId, { progress: 0, updating: false, indeterminate: false, status: 'done', current: fwVer, hasUpdate: false, target: null, lastTs: now });
          return;
        }
        if (state.updating) {
          const latest = state.latest || '';
          if (!latest || cmpVer(fwVer, latest) >= 0) {
            setFwState(deviceId, { progress: 0, updating: false, indeterminate: false, status: 'done', current: fwVer, hasUpdate: false, target: null, lastTs: now });
            return;
          }
        }
        setFwState(deviceId, { current: fwVer, lastTs: now });
        return;
      }

      const mClaim = topic.match(/^sonoff\/claim\/([^/]+)$/i);
      if (mClaim) {
        const mac = String(mClaim[1]).toUpperCase();
        const deviceId = macToDeviceId[mac];
        if (!deviceId) return;
        const s = fwByIdRef.current[deviceId] || {};
        if (s.updating) {
          setFwState(deviceId, { status: s.status || 'start', updating: true, indeterminate: true, lastTs: Date.now() });
        }
      }
    };

    const sub = () => {
      topics.forEach(t => client.subscribe(t, err => {
        if (err) console.error('[MQTT] subscribe OTA/info/claim err:', t, err?.message || err);
      }));
      client.on('message', handler);
    };
    if (client.connected && !client.disconnecting) sub();
    else client.once('connect', sub);

    return () => {
      client?.off('message', handler);
      if (client.connected && !client.disconnecting) {
        topics.forEach(t => client.unsubscribe(t, () => {}));
      }
    };
  }, [macToDeviceId, checkDeviceFw]);

  // Watchdog OTA
  useEffect(() => {
    const iv = setInterval(() => {
      const now = Date.now();
      const cur = fwByIdRef.current || {};
      Object.keys(cur).forEach(idStr => {
        const id = Number(idStr);
        const s = cur[id] || {};
        if ((s.updating || (s.progress || 0) > 0 || s.indeterminate) && s.lastTs && (now - s.lastTs) > 120000) {
          setFwState(id, { updating: false, progress: 0, indeterminate: false, status: 'done' });
        }
      });
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  // (Optionnel – HTTP polling si un endpoint existe)
  const pollOtaProgress = useCallback(async (device) => {
    if (!FW_OTA_PROGRESS) return;
    const id = device.id;
    const start = Date.now();
    const MAX_MS = 5 * 60 * 1000;
    const STEP_MS = 3000;
    const tryFetchOnce = async () => {
      try {
        const r = await fetch(`${FW_OTA_PROGRESS}?identifier=${encodeURIComponent(normalizeMac(device.identifier))}&ts=${Date.now()}`, {
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache' }
        });
        if (!r.ok) return null;
        return await r.json();
      } catch { return null; }
    };
    while (mountedRef.current && Date.now() - start < MAX_MS) {
      const j = await tryFetchOnce();
      if (!j) break;
      const status = j.status || j.state || '';
      const progress = Number(j.progress ?? j.percent ?? j.percentage ?? 0);
      const indet = !(Number.isFinite(progress)) || progress <= 0;
      setFwState(id, { updating: true, progress: Math.max(0, Math.min(100, progress || 0)), indeterminate: indet, status });
      if (status === 'done' || status === 'success' || progress >= 100) {
        setFwState(id, { updating: false, progress: 0, indeterminate: false, status: 'done' });
        const dev = flattenDevices(myDevicesRef.current).find(d => d.id === id);
        if (dev) setTimeout(() => checkDeviceFw(dev, true), 15000);
        return;
      }
      if (status === 'error' || status === 'failed') {
        setFwState(id, { updating: false, indeterminate: false, status: 'error', error: 'ota_failed' });
        return;
      }
      await new Promise(r => setTimeout(r, STEP_MS));
    }
  }, [checkDeviceFw]);

  const triggerOta = useCallback(async (device) => {
    if (!device) return { ok: false, error: 'missing_device' };
    const id = device.id;
    const mac = normalizeMac(device.identifier);
    const fwName = device.firmware_name || device.firmware || '';
    const model = extractModel(fwName) || 'LDJ_MiniR2';

    setFwState(id, { checking: true, error: null });

    let ok = false, j = null;
    try {
      let res = await fetch(FW_REQUEST_OTA, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: id, identifier: mac, model })
      });
      j = await res.json().catch(() => ({}));
      ok = (typeof j?.ok === 'undefined') ? res.ok : !!j.ok;
      if (!ok) {
        res = await fetch(FW_REQUEST_OTA, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ device_id: String(id), identifier: mac, model })
        });
        j = await res.json().catch(() => ({}));
        ok = (typeof j?.ok === 'undefined') ? res.ok : !!j.ok;
      }
    } catch {}

    const target = (fwByIdRef.current[id]?.latest) || null;
    // IMPORTANT: démarrer en indéterminé (spinner) — pas de faux "5 %"
    setFwState(id, { checking: false, updating: ok, progress: ok ? 0 : 0, indeterminate: !!ok, status: ok ? 'starting' : undefined, target });
    if (ok) {
      openOtaWindow(otaWindowRef, mac);
      pollOtaProgress(device); // (ne fait rien si FW_OTA_PROGRESS = null)
      const dev = device;
      setTimeout(() => checkDeviceFw(dev, true), 15000);
      return { ok: true };
    }
    return { ok: false, error: 'ota_trigger_failed', details: j?.error || j?.msg };
  }, [pollOtaProgress, checkDeviceFw]);

  // ==== Chargement infos compte (depuis /user/* ; fallback props user) ====
  const loadAccount = useCallback(async () => {
    setAccountLoading(true);
    try {
      // 1) essai endpoint me.php (recommandé)
      const candidates = [
        `${API_BASE}/user/me.php`,
        `${API_BASE}/user/get_profile.php`,
        `${API_BASE}/auth/check_session.php`,
        `${API_BASE}/user/profile.php`,
      ];
      let got = null;
      for (const url of candidates) {
        try {
          const { ok, json } = await fetchJSON(url);
          if (!ok || !json) continue;
          // différents formats possibles
          const u = json.user || json.data || json;
          const username = u.username || u.name || '';
          const email    = u.email || '';
          const created  = u.created_at || '';
          if (username || email) { got = { username, email, created_at: created }; break; }
        } catch {}
      }
      // 2) fallback immédiat sur props user (renvoyé par login.php)
      if (!got) {
        got = {
          username: user?.username || user?.name || '',
          email: user?.email || '',
          created_at: user?.created_at || ''
        };
      }
      setAccount({
        username: got.username || '—',
        email: got.email || '—',
        created_at: got.created_at || '—'
      });
    } finally {
      setAccountLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (accountOpen) loadAccount();
  }, [accountOpen, loadAccount]);

  // ---------- Rendu ----------
  const isMobile = windowWidth <= 600;

  const editingDevice = editingId
    ? flattenDevices(myDevices).find(d => d.id === editingId)
    : null;

  const renderOtaInline = (fw) => {
    if (!(fw.updating || fw.progress > 0 || fw.indeterminate)) return null;
    // mini UI pour la ligne d’appareil
    return fw.indeterminate ? (
      <div className="fw-inline">
        <span className="fw-spinner" title="Mise à jour…">⏳</span>
      </div>
    ) : (
      <div className="progress-out">
        <div className="progress-in" style={{ width: `${Math.max(0, Math.min(100, fw.progress || 0))}%` }} />
      </div>
    );
  };

  return (
    <div className="page">
      <img className="logo" src="/logo-fond-transparent.png" alt="Logo Lumieres de Jardin" />
      <div className="title-row">
        <h2 className="title">Ton espace LUMIERES DE JARDIN</h2>
        {!accountOpen && (
          <button className="account-btn" onClick={() => setAccountOpen(true)}>⚙️</button>
        )}
      </div>

      {/* ===== Modale “Mon compte” centrée ===== */}
      {accountOpen && (
        <div className="overlay overlay--center" onClick={() => setAccountOpen(false)}>

      {/* ===== Modale changement mot de passe ===== */}
      {showPwModal && (
        <div className="overlay overlay--center" onClick={() => setShowPwModal(false)}>
          <div className="card card--narrow" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPwModal(false)} aria-label="Fermer">✖</button>
            <h3>Changer le mot de passe</h3>
            <div className="row"><span className="row-label">Mot de passe actuel :</span>
              <input type="text" className="row-input" value={oldPw} onChange={e => setOldPw(e.target.value)} />
            </div>
            <div className="row"><span className="row-label">Nouveau :</span>
              <input type="text" className="row-input" value={newPw} onChange={e => setNewPw(e.target.value)} />
            </div>
            <div className="row"><span className="row-label">Confirmer :</span>
              <input type="text" className="row-input" value={newPw2} onChange={e => setNewPw2(e.target.value)} />
            </div>
            <div className="dialog-actions" style={{marginTop: 12}}>
              <button className="btn btn-light" onClick={() => setShowPwModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={async () => {
                if (newPw !== newPw2) { alert('Les mots de passe ne correspondent pas'); return; }
                if (!oldPw || !newPw) { alert('Champs manquants'); return; }
                try {
                  // 1) Essai JSON
                  let ok = false, msg = '';
                  try {
                    const r = await fetch(`${API_BASE}/user/change_password.php`, {
                      method:'POST',
                      credentials:'include',
                      headers:{'Content-Type':'application/json'},
                      body: JSON.stringify({ old_password: oldPw, new_password: newPw, confirm_password: newPw2 })
                    });
                    ok = r.ok;
                    try { const j = await r.json(); if (j?.ok === false) { ok = false; msg = j?.error || ''; } } catch {}
                  } catch(e) {}
                  // 2) Fallback x-www-form-urlencoded (très courant en PHP)
                  if (!ok) {
                    try {
                      const r2 = await fetch(`${API_BASE}/user/change_password.php`, {
                        method:'POST',
                        credentials:'include',
                        headers:{'Content-Type':'application/x-www-form-urlencoded'},
                        body: new URLSearchParams({ old_password: oldPw, new_password: newPw, confirm_password: newPw2 }).toString()
                      });
                      ok = r2.ok;
                      try { const j2 = await r2.json(); if (j2?.ok === false) { ok = false; msg = j2?.error || ''; } } catch {}
                    } catch(e) {}
                  }
                  if (ok) {
                    alert('Mot de passe mis à jour.');
                    setShowPwModal(false);
                    setOldPw(''); setNewPw(''); setNewPw2('');
                  } else {
                    alert(msg || "Échec du changement de mot de passe. Vérifie l'actuel.");
                  }
                } catch(e) {
                  alert("Erreur réseau / serveur lors du changement de mot de passe");
                }
              }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
>
          <div className="card card--narrow" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setAccountOpen(false)} aria-label="Fermer">✖</button>
            <h3>Mon compte</h3>

            <div className="row"><span className="row-label">Nom d’utilisateur :</span><span>{accountLoading ? '…' : account.username || '—'}</span></div>
            <div className="row"><span className="row-label">E-mail :</span><span>{accountLoading ? '…' : account.email || '—'}</span></div>
            <div className="row"><span className="row-label">Créé le :</span><span>{accountLoading ? '…' : safeToLocaleDate(account.created_at)}</span></div>

            <div style={{ marginTop: 12 }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowPwModal(true)}
              >
                Changer le mot de passe
              </button>
            </div>

            <div className="dialog-actions" style={{ marginTop: 14 }}>
              <button className="btn btn-light" onClick={() => setAccountOpen(false)}>Fermer</button>
              <button
                className="btn btn-warning"
                onClick={async () => {
                  try {
                    await fetch(`${API_BASE}/user/logout.php`, { method: 'POST', credentials: 'include' });
                  } catch {}
                  if (onLogout) onLogout();
                  window.location.href = '/';
                }}
              >
                Déconnexion
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  alert("Suppression de compte : à implémenter côté backend si besoin.");
                }}
              >
                Supprimer le compte
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Édition nom/adresse device */}
      {editingDevice && (
        <div className="overlay" onClick={() => setEditingId(null)}>
          <div className="card card--edit" onClick={e => e.stopPropagation()}>
            <form
              className="ldj-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const payload = { ...editFields, id: editingId };
                try {
                  await fetch(`${API_BASE}/devices/update_device_infos.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    credentials: 'include'
                  });
                  setMyDevices(prev => prev.map(group => ({
                    ...group,
                    devices: group.devices.map(d =>
                      d.id === editingId
                        ? {
                            ...d,
                            name: payload.name ?? d.name,
                            home_name: payload.home_name ?? d.home_name,
                            location_name: payload.location_name ?? d.location_name,
                            city_name: payload.city_name ?? d.city_name,
                            code_postal: payload.code_postal ?? d.code_postal,
                            country: payload.country ?? d.country
                          }
                        : d
                    )
                  })));
                } finally {
                  setEditingId(null);
                  setEditFields({});
                  fetchMyDevices();
                }
              }}
            >
              <h4 className="form-title">Modifier l’appareil</h4>
              {[
                { label: 'Appareil', key: 'name', placeholder: 'Nommez votre appareil' },
                { label: 'Maison', key: 'home_name', placeholder: 'Le nom de votre maison' },
                { label: 'Zone', key: 'location_name', placeholder: 'Zone où se trouve l’appareil' },
                { label: 'Ville', key: 'city_name', placeholder: 'Nécessaire pour Soleil' },
                { label: 'Code postal', key: 'code_postal', placeholder: 'Nécessaire pour Soleil' }
              ].map(({ label, key, placeholder }) => (
                <label className="row" key={key}>
                  <span className="row-label">{label} :</span>
                  <input
                    type="text"
                    value={editFields[key] || ''}
                    onChange={e => setEditFields(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="row-input"
                  />
                </label>
              ))}

              {/* Pays */}
              <label className="row">
                <span className="row-label">Pays :</span>
                <div className="row-select">
                  <Select
                    value={COUNTRY_OPTIONS.find(opt => opt.value === (editFields.country || '')) || null}
                    options={COUNTRY_OPTIONS}
                    onChange={opt => setEditFields(f => ({ ...f, country: opt?.value || '' }))}
                    getOptionLabel={opt => (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <FlagIcon code={opt.flag} size={16} />
                        <span>{opt.label}</span>
                      </span>
                    )}
                    getOptionValue={opt => opt.value}
                    isClearable
                    placeholder="Nécessaire pour Soleil"
                  />
                </div>
              </label>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">💾</button>
                <button type="button" className="btn btn-danger" onClick={() => setEditingId(null)}>❌</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loadingDevices ? (
        <div className="loading">🔍 Chargement de vos appareils...</div>
      ) : (
        <div className="devices-container">
          {myDevices.map(group => (
            <div className="group" key={group.location_id}>
              {group.devices.map(device => {
                const online =
                  onlineStates[device.id]?.online !== undefined
                    ? onlineStates[device.id].online
                    : Number(device.online) === 1;

                let relay;
                if (typeof relayStates[device.id] !== 'undefined') {
                  relay = relayStates[device.id];
                } else if (typeof device.relay_state !== 'undefined') {
                  relay = device.relay_state === 1 || device.relay_state === '1';
                }

                const fw = fwById[device.id] || {};
                const infoDotCls = `info-dot${fw.hasUpdate ? ' has-update' : ''}`;

                return (
                  <div className="device-row" key={device.id}>
                    <div className="left-col">
                      <div className="name-line">
                        <button
                          type="button"
                          className="name-btn"
                          title={device.name || device.device_name}
                          onClick={e => {
                            e.stopPropagation();
                            setEditingId(device.id);
                            setEditFields({
                              name: device.name || '',
                              home_name: device.home_name || '',
                              location_name: device.location_name || '',
                              city_name: device.city_name || '',
                              code_postal: device.code_postal || '',
                              country: (device.country ?? '')
                            });
                          }}
                        >
                          <span className="ellipsis">{device.name || device.device_name}</span>
                          {fw.hasUpdate && (
                            <span
                              className={infoDotCls}
                              role="button"
                              title="Nouvelle version disponible"
                              onClick={(e) => { e.stopPropagation(); setFwPromptForId(device.id); }}
                            >i</span>
                          )}
                          {fw.checking && (<span className="fw-spinner" title="Vérification…">⏳</span>)}
                        </button>
                      </div>

                      <div className="meta">
                        <img
                          className="wifi"
                          src={online ? '/img/wifi_online.png' : '/img/wifi_offline.png'}
                          alt={online ? 'Appareil en ligne' : 'Appareil hors ligne'}
                        />
                        <span className={`state ${relay === true ? 'state-on' : 'state-off'}`}>
                          {relay === true ? 'Allumé' : relay === false ? 'Éteint' : ''}
                        </span>
                      </div>

                      {renderOtaInline(fw)}
                    </div>

                    <div className="actions">
                      <img
                        className={`power ${online && !fw.updating ? 'clickable' : 'disabled'}`}
                        src={relay ? '/img/power_on.png' : '/img/power_off.png'}
                        alt={relay ? 'On' : 'Off'}
                        onClick={e => {
                          e.stopPropagation();
                          if (online && !fw.updating) toggleRelay(device.id, relay);
                        }}
                      />
                      <button
                        className="btn btn-planning"
                        title="Voir plannings"
                        onClick={e => { e.stopPropagation(); setSelectedClaimedId(device.id); }}
                        disabled={fw.updating}
                      >
                        Plannings
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Modal DeviceCard */}
          {selectedClaimedId && (
            <div className="overlay" onClick={() => setSelectedClaimedId(null)}>
              <div className="card card--device" onClick={e => e.stopPropagation()}>
                <div className="card-padding">
                  <DeviceCard
                    device={flattenDevices(myDevices).find(d => d.id === selectedClaimedId)}
                    relayState={relayStates[selectedClaimedId]}
                    isOnline={onlineStates[selectedClaimedId]?.online}
                    onToggleRelay={toggleRelay}
                    onDelete={() => handleDelete(selectedClaimedId)}
                    onClaimed={() => setSelectedClaimedId(null)}
                    onNameChange={(id, name, home, location) => {
                      setMyDevices(prev =>
                        prev.map(group => ({
                          ...group,
                          devices: group.devices.map(d =>
                            d.id === id ? { ...d, name, home_name: home, location_name: location } : d
                          )
                        }))
                      );
                    }}
                    onClose={() => setSelectedClaimedId(null)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Wizard ajout */}
          {showWizard && (
            <div className="overlay" onClick={() => setShowWizard(false)}>
              <div className="card card--wide" onClick={e => e.stopPropagation()}>
                <div className="wizard-split">
                  <div className="wizard-col wizard-procedure">
                    <NewDeviceWizard
                      onCancel={() => setShowWizard(false)}
                      onDeviceAdded={() => { fetchMyDevices(); setShowWizard(false); }}
                    />
                  </div>
                  <div className="wizard-col wizard-discovery">
                    <div className="wizard-head">
                      <h4>Appareils détectés (à appairer)</h4>
                      <button className="btn btn-light" onClick={fetchUnclaimed} disabled={loadingUnclaimed}>
                        {loadingUnclaimed ? '…' : '🔄'}
                      </button>
                    </div>
                    <p className="wizard-hint">
                      Après <b>appui long 5s</b> puis configuration Wi-Fi, l’appareil apparaît ici.
                      Clique <b>Appairer</b> pour l’ajouter à ton compte.
                    </p>
                    {pairMsg && <div className="callout info">{pairMsg}</div>}
                    <div className="detected-list">
                      {loadingUnclaimed ? (
                        <div className="muted">Chargement…</div>
                      ) : unclaimed.length === 0 ? (
                        <div className="muted">Aucun appareil en attente (dernières 15 minutes).</div>
                      ) : (
                        unclaimed.map(u => {
                          const mac = normalizeMac(u.identifier);
                          return (
                            <div className="detected-item" key={mac}>
                              <div className="detected-meta">
                                <div className="detected-mac">{mac}</div>
                                <div className="detected-sub">
                                  {u.firmware ? `FW: ${u.firmware}` : ''} {u.ip ? `• IP: ${u.ip}` : ''} {u.age_sec != null ? `• vu il y a ${fmtAge(u.age_sec)}` : ''}
                                </div>
                              </div>
                              <button
                                className="btn btn-primary"
                                onClick={() => doPair(mac)}
                                disabled={pairingMac === mac}
                                title="Associer cet appareil à mon compte"
                              >
                                {pairingMac === mac ? '…' : 'Appairer'}
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Appairage manuel */}
                    <div className="manual">
                      <div className="manual-title">Appairage manuel</div>
                      <div className="manual-row">
                        <input
                          className="row-input"
                          type="text"
                          placeholder="MAC (ex: A1B2C3D4E5F6 ou A1:B2:...)"
                          value={manualMac}
                          onChange={(e) => { setManualError(''); setManualMac(e.target.value); }}
                        />
                        <button
                          className="btn btn-warning"
                          onClick={() => {
                            const n = normalizeMac(manualMac);
                            if (!n || n.length !== 12) { setManualError('MAC invalide (12 hex).'); return; }
                            doPair(n);
                          }}
                          disabled={!!pairingMac}
                          title="Associer par MAC"
                        >
                          Appairer
                        </button>
                      </div>
                      {manualError && <div className="error-text">{manualError}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bas de page : ajouter / supprimer */}
          {!showWizard && (
            <div className="bottom-bar">
              <div className="add-box">
                <DeviceListBox
                  label="➕ Nouvel appareil"
                  onClick={() => setShowWizard(true)}
                  isSelected={showWizard}
                />
              </div>

              <button className="btn btn-delete" onClick={() => setShowDeleteDialog(true)}>
                🗑️ Supprimer un appareil
              </button>
            </div>
          )}
        </div>
      )}

      {/* Suppression */}
      {showDeleteDialog && (
        <div className="overlay" onClick={() => setShowDeleteDialog(false)}>
          <div className="card card--narrow" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowDeleteDialog(false)} aria-label="Fermer">✖</button>
            <h3>Supprimer un appareil</h3>
            <p className="muted">Sélectionnez l’appareil à retirer de votre compte. Cette action supprime aussi ses plannings et sa localisation.</p>
            <div className="row-select">
              <Select
                placeholder="Choisir un appareil…"
                options={deleteOptions}
                value={deleteOptions.find(o => o.value === deleteId) || null}
                onChange={opt => setDeleteId(opt ? opt.value : null)}
                isClearable
              />
            </div>
            <div className="dialog-actions">
              <button className="btn btn-light" onClick={() => setShowDeleteDialog(false)}>Annuler</button>
              <button
                className="btn btn-danger"
                onClick={() => { if (!deleteId) { alert('Sélectionnez un appareil.'); return; } handleDelete(deleteId); }}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pop-up ON/OFF */}
      {toast && (
        <div className="overlay overlay--center">
          <div className="toast-card">
            <button className="modal-close" onClick={() => setToast(null)} aria-label="Fermer">✖️</button>
            <div className="toast-title">{toast.name}</div>
            <div className={`toast-state ${toast.isOn ? 'on' : 'off'}`}>
              {toast.isOn ? 'Allumé' : 'Éteint'}
            </div>
          </div>
        </div>
      )}

      {/* Pop-up Firmware */}
      {fwPromptForId != null && (() => {
        const flat = flattenDevices(myDevices);
        const dev = flat.find(d => d.id === fwPromptForId);
        const fw = fwById[fwPromptForId] || {};
        if (!dev) return null;
        if (!fw.hasUpdate && !fw.updating && !fw.indeterminate && !(fw.progress > 0)) return null;
        const mac = normalizeMac(dev.identifier);

        const label =
          fw.status === 'error' ? 'Erreur OTA' :
          fw.status === 'reboot' ? 'Redémarrage…' :
          (fw.updating || fw.indeterminate || fw.progress > 0) ? 'Mise à jour en cours…' :
          'Mise à jour disponible';

        return (
          <div className="overlay" onClick={() => setFwPromptForId(null)}>
            <div className="card card--narrow" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setFwPromptForId(null)} aria-label="Fermer">✖</button>
              <h3>Mise à jour du firmware</h3>
              <p className="muted">
                {dev.name || dev.device_name} ({mac})<br/>
                {fw.current ? <>Actuel : <b>{fw.current}</b><br/></> : null}
                {fw.latest ? <>Disponible : <b>{fw.latest}</b><br/></> : null}
              </p>

              {(fw.updating || fw.progress > 0 || fw.indeterminate) && (
                <div className="fw-progress">
                  <div className="fw-progress-label">
                    {label} {(!fw.indeterminate && fw.progress) ? `${Math.round(fw.progress)}%` : ''}
                  </div>
                  {fw.indeterminate ? (
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span className="fw-spinner" title="Téléchargement…">⏳</span>
                      <span className="muted">Téléchargement…</span>
                    </div>
                  ) : (
                    <div className="progress-out">
                      <div className="progress-in" style={{ width: `${Math.max(0, Math.min(100, fw.progress || 0))}%` }} />
                    </div>
                  )}
                </div>
              )}

              <div className="dialog-actions">
                <button className="btn btn-light" onClick={() => setFwPromptForId(null)} disabled={fw.updating || fw.indeterminate}>Fermer</button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!fw.hasUpdate) return;
                    const r = await triggerOta(dev);
                    if (r.ok) {
                      // Pas d'alert bloquante. On garde la modale ouverte, on montre ⏳ puis la barre.
                      setFwState(dev.id, { updating: true, indeterminate: true, status: 'starting', progress: 0 });
                    } else {
                      alert("Impossible de déclencher l'OTA (serveur). Réessaie.");
                    }
                  }}
                  disabled={!fw.hasUpdate || fw.checking || fw.updating || fw.indeterminate}
                  title="Déclencher la mise à jour OTA"
                >
                  {fw.checking ? '…' : (fw.updating || fw.indeterminate ? 'En cours…' : 'Mettre à jour')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default DashboardPage;

